/**
 * PersonAvatar — photo when available, otherwise name initials.
 */
import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { initials } from '../lib/format.js';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const sizeClass: Record<Size, string> = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-11 w-11 text-sm',
  lg: 'h-16 w-16 text-xl',
  xl: 'h-20 w-20 text-xl',
};

export function PersonAvatar(props: {
  name: string;
  src?: string | null;
  size?: Size;
  className?: string;
  /** Darker/inverted initials style used on directory cards. */
  tone?: 'default' | 'inverse';
}) {
  const size = props.size ?? 'sm';
  const tone = props.tone ?? 'default';
  const label = initials(props.name) || '?';
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [props.src]);

  if (props.src && !failed) {
    return (
      <img
        src={props.src}
        alt=""
        onError={() => setFailed(true)}
        className={clsx(
          'shrink-0 rounded-full border border-[var(--color-rule)] object-cover',
          sizeClass[size],
          props.className,
        )}
      />
    );
  }

  return (
    <div
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full border border-[var(--color-rule)] font-bold',
        sizeClass[size],
        tone === 'inverse'
          ? 'bg-[var(--color-ink)] text-[var(--color-surface)]'
          : 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]',
        props.className,
      )}
      aria-hidden
    >
      {label}
    </div>
  );
}

/**
 * Employee avatar proxy URL.
 * Always returns a URL when employeeId is set — the API falls back to the
 * linked user's pictureUrl. PersonAvatar shows initials if the request 404s.
 */
export function employeeAvatarSrc(
  employeeId: string | null | undefined,
  avatarStorageKey?: string | null,
): string | null {
  if (!employeeId) return null;
  if (avatarStorageKey?.startsWith('avatars/')) {
    const version = avatarStorageKey.split('/').pop() ?? '1';
    return `/api/employees/${encodeURIComponent(employeeId)}/avatar?v=${encodeURIComponent(version)}`;
  }
  if (avatarStorageKey && (/^https?:\/\//i.test(avatarStorageKey) || avatarStorageKey.startsWith('/'))) {
    return avatarStorageKey;
  }
  return `/api/employees/${encodeURIComponent(employeeId)}/avatar`;
}
